import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { UnitList } from '@r6digital/rapidstor-types/react/rtk-query/UnitList';
import {
    BaseReservation,
    RapidStorReservationRequest,
} from '@r6digital/rapidstor-types/types/Reservation';

import { skipToken } from '@reduxjs/toolkit/query';
import { DealSelectorVariant } from '@r6digital/rapidstor-types/react/entities/Journey';
import {
    useGetPersonasQuery,
    useGetUnitListByTypeQuery,
    usePostReservationMutation,
} from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';
import { getDefaultNeededDate, PERSONA_LOCK_COOKIE, toNeededDate } from '@/core/utils/rapidstor';
import { fadeOut } from '@/core/utils/ui';
import { PersonaType } from '@/journey-based-listing/screens/index';

import store from '@/store';
import {
    ReservationData,
    selectReservationData,
    selectUnitData,
    setContactCard,
    setPersonaId,
    setReservationData,
    setUnitData,
    UnitData,
} from '@/store/bookingSlice';

import { FormType } from '@/core/components/elements/contact-card';

import { AnalyticsContext } from './AnalyticsProvider';

export interface JourneyContextProps {
    location: Location | null;
    unitData: UnitData | null;
    reservationData: ReservationData | null;
    selectedUnitType: UnitType | undefined;
    selectedDescriptor: Descriptor | undefined;
    selectionTypes: PersonaType[] | undefined;
    persona: string | undefined;
    unitList: UnitList | undefined;
    unitListLoading: boolean | undefined;
    unitListSuccess: boolean | undefined;
    unavailableDescriptorIds: string[];
    descriptorList: Descriptor[];
    rapidstorMainColor: string | null;
    toggleContactCard: (
        type: FormType,
        selectedUnitType?: UnitType,
        deal?: string,
    ) => void;
    setSelectedUnitType: (unitType: UnitType) => void;
    setSelectedDescriptor: (descriptor: Descriptor) => void;
    setSelectionTypes: (types: PersonaType[]) => void;
    setPersona: (personaId: string | undefined) => void;
    setUnitData: (unitData: UnitData) => void;
    setReservationData: (reservationData: ReservationData) => void;
    setupSliceData: (
        reservationData: ReservationData,
        unitData: UnitData,
    ) => void;
    setUnavailableDescriptorIds: (ids: string[] | ((prev: string[]) => string[])) => void;
    handleMakeReservation: (
        payload: RapidStorReservationRequest,
        descriptorId: string,
    ) => Promise<BaseReservation | false>;
    dealSelectorVariant?: DealSelectorVariant;
}

export const JourneyContext = createContext<JourneyContextProps>({
    location: null,
    unitData: null,
    reservationData: null,
    persona: undefined,
    selectionTypes: undefined,
    unitList: undefined,
    unitListLoading: undefined,
    unitListSuccess: undefined,
    dealSelectorVariant: undefined,

    selectedUnitType: undefined,
    selectedDescriptor: undefined,
    unavailableDescriptorIds: [],
    descriptorList: [],

    rapidstorMainColor: null,

    toggleContactCard: () => null,
    setSelectedUnitType: () => null,
    setSelectedDescriptor: () => null,
    setSelectionTypes: () => null,
    setPersona: () => null,
    setUnitData: () => null,
    setReservationData: () => null,
    setupSliceData: () => null,
    setUnavailableDescriptorIds: () => null,
    handleMakeReservation: async () => false,
});

export function JourneyContextProvider({
    children,
    location,
}: PropsWithChildren<{ location: Location }>) {
    const unitData = useSelector(selectUnitData);
    const reservationData = useSelector(selectReservationData);
    const navigate = useNavigate();
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    const [cookies, setCookie] = useCookies(['persona', PERSONA_LOCK_COOKIE]);

    // Persona hierarchy: query param → config → cookie → undefined
    const initialPersonaId = new URLSearchParams(window.location.search).get('personaId')
        || window.rapidstor_settings.rapidstor_options.persona
        || cookies.persona
        || undefined;

    const [makeReservation] = usePostReservationMutation();
    const [persona, setPersona] = useState<string | undefined>(initialPersonaId);
    const [personaResolved, setPersonaResolved] = useState(!!initialPersonaId);
    const [selectionTypes, setSelectionTypes] = useState<
    PersonaType[] | undefined
    >();
    const [selectedUnitType, setSelectedUnitType] = useState<
    UnitType | undefined
    >();
    const [selectedDescriptor, setSelectedDescriptor] = useState<
    Descriptor | undefined
    >();
    const [unavailableDescriptorIds, setUnavailableDescriptorIds] = useState<
    string[]
    >([]);

    // Sync persona to cookie and store when it changes
    useEffect(() => {
        if (persona) {
            setCookie('persona', persona, {
                path: '/',
                maxAge: 60 * 60 * 24,
            });
            store.dispatch(setPersonaId(persona));
        }
    }, [persona, setCookie]);

    const {
        data: personas,
        error: personasError,
        isError: didPersonasFail,
        isSuccess: didPersonasSucceed,
    } = useGetPersonasQuery({
        locationCode: location.locationCode,
        corpCode: location.corpCode,
    });

    useEffect(() => {
        if (personasError && didPersonasFail) {
            Logger.warn(
                `[JourneyContextProvider] Personas Fetch Error: ${JSON.stringify(
                    personasError,
                )}`,
                location,
            );
            setPersonaResolved(true);
        }
        if (didPersonasSucceed && personas) {
            setSelectionTypes(personas.filter((p) => p.available));

            // Match query params against persona parameters to auto-select a persona
            // Check both before the hash (window.location.search) and after (hash query string)
            if (!persona) {
                const queryParams = new URLSearchParams(window.location.search);
                const hashQuery = window.location.hash.split('?')[1];
                if (hashQuery) {
                    new URLSearchParams(hashQuery).forEach((value, key) => {
                        if (!queryParams.has(key)) {
                            queryParams.set(key, value);
                        }
                    });
                }
                const matchedPersona = personas.find((p) => p.parameters?.some(
                    (param) => queryParams.get(param.name) === param.value,
                ));
                if (matchedPersona?._id) {
                    // Persona resolved from URL params — lock the journey to it.
                    setCookie(PERSONA_LOCK_COOKIE, '1', { path: '/', maxAge: 60 * 60 * 24 });
                    setPersona(matchedPersona._id);
                    return;
                }
            }
            setPersonaResolved(true);
        }
    }, [didPersonasFail, didPersonasSucceed, location, personas, personasError, persona, setCookie]);

    const {
        data: unitList,
        error: unitListError,
        isError: didUnitListFail,
        isFetching: unitListLoading,
        isSuccess: didUnitListSucceed,
    } = useGetUnitListByTypeQuery(personaResolved ? {
        locationCodes: [location.locationCode],
        persona,
        unitTypeNames: window.rapidstor_settings.rapidstor_options.unitTypeNames,
        unitTypeRegex: window.rapidstor_settings.rapidstor_options.unitTypeNames
            ? undefined
            : window.rapidstor_settings.rapidstor_options.unitTypeRegex,
    } : skipToken);

    if (unitListError && didUnitListFail) {
        Logger.warn(
            `[JourneyContextProvider] UnitList Fetch Error: ${JSON.stringify(
                unitListError,
            )}`,
            location,
        );
    }

    const setUnitDataInStore = (newUnitData: UnitData) => {
        store.dispatch(setUnitData(newUnitData));
    };

    const setReservationDataInStore = (newReservationData: ReservationData) => {
        store.dispatch(setReservationData(newReservationData));
    };

    /**
   * Toggles the contact card.
   *
   * @param type
   * @param selectedUnitType
   * @param deal
   */
    const toggleContactCard = (
        type: FormType,
        newSelectedUnitType?: UnitType,
        deal?: string,
    ) => {
        store.dispatch(
            setContactCard({
                animate: 'start',
                type,
                unitType: newSelectedUnitType ?? null,
                isDeal: !!deal || false,
                deal: deal || null,
            }),
        );
    };

    /** Setup inital slice data */
    const setupSliceData = (
        newReservationData: ReservationData,
        newUnitData: UnitData,
    ) => {
        setReservationDataInStore(newReservationData);
        setUnitDataInStore(newUnitData);
    };

    /**
   * Sets the units array as empty for the descriptors that are unavailable.
   *
   * @param descriptors
   *
   * @returns {Descriptor[]} Modified Descriptor List
   */
    const unavailableDescriptorHandler = (
        newDescriptors: Descriptor[],
    ): Descriptor[] => newDescriptors.map((newDescriptor) => {
        if (unavailableDescriptorIds.includes(newDescriptor._id)) {
            return {
                ...newDescriptor,
                unitTypes: newDescriptor.unitTypes.map((newUnitType) => ({
                    ...newUnitType,
                    units: [],
                })),
            };
        }
        return newDescriptor;
    });

    /**
   * Make reservation handler.
   *
   * @param payload
   * @param descriptorId
   *
   * @returns {Promise<BaseReservation | false>}
   */
    const handleMakeReservation = async (
        payload: RapidStorReservationRequest,
        descriptorId: string,
    ): Promise<BaseReservation | false> => {
        try {
            const formattedPayload = {
                ...payload,
                payload: {
                    ...payload.payload,
                    neededDate: toNeededDate(
                        payload.payload.neededDate
                        ?? getDefaultNeededDate(location, moment()),
                    ),
                },
            };
            return await makeReservation(formattedPayload).unwrap();
        } catch (error) {
            fadeOut();
            firebaseLogEvent('unit_unavailable_for_move_in', {});
            setUnavailableDescriptorIds((prev) => [
                ...prev,
                descriptorId,
            ]);
            setTimeout(() => {
                navigate('/unit-listing', {
                    state: {
                        unitUnavailableAtMoveIn: true,
                        unavailableDescriptor: descriptorId,
                    },
                });
            }, 300);
            return false;
        }
    };

    /**
   * Core logic for building the final descriptor list.
   *
   * @return {Descriptor[]} Descriptor List
   */
    const descriptorList = useMemo((): Descriptor[] => {
        if (!unitList || unitListLoading) {
            return [];
        }

        let { descriptors } = unitList;

        if (location.rapidstor.carouselListing) {
            const descriptorsUseForCarousel = descriptors.filter(
                (descriptor) => descriptor.useForCarousel,
            );

            if (descriptorsUseForCarousel.length) {
                descriptors = descriptorsUseForCarousel;
            }
        }

        return unavailableDescriptorHandler(descriptors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        location.rapidstor.carouselListing,
        unitList,
        unitListLoading,
        unavailableDescriptorIds,
    ]);

    const rapidstorMainColor = useMemo(() => window.rapidstor_settings?.app_config?.journey?.themeColor ?? location.skin.mainColour ?? null, [location.skin.mainColour]);

    const dealSelectorVariant = window.rapidstor_settings?.rapidstor_options?.dealSelectorVariant;

    return (
        <JourneyContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
                location,
                toggleContactCard,
                unitData,
                unitListLoading,
                unitListSuccess: didUnitListSucceed,
                reservationData,
                selectedUnitType,
                selectedDescriptor,
                selectionTypes,
                persona,
                unitList,
                unavailableDescriptorIds,
                descriptorList,
                rapidstorMainColor,
                dealSelectorVariant,
                setPersona,
                setSelectionTypes,
                setSelectedUnitType,
                setSelectedDescriptor,
                setUnitData: setUnitDataInStore,
                setReservationData: setReservationDataInStore,
                setupSliceData,
                setUnavailableDescriptorIds,
                handleMakeReservation,
            }}
        >
            {children}
        </JourneyContext.Provider>
    );
}
